import { message, notification, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import { JobPostingDetail } from '../types';
import { buildRecruitmentProcessQueryParam } from '../utils';
import MoveCandidateStageModal, {
  MoveCandidateStageFormValues,
  MoveCandidateStageModalProps,
} from './MoveCandidateStageModal';

interface BulkMoveCandidateStageModalProps
  extends Omit<MoveCandidateStageModalProps, 'helmetTitle'> {
  jobPostingId: JobPostingDetail['id'];
  onSuccess?: (
    data: AxiosResponse<unknown, unknown> | void,
    variables: MoveCandidateStageFormValues,
    context: unknown
  ) => void | Promise<unknown>;
  functionId?: JobPostingDetail['function_id'];
  totalCount?: number;
}

const BulkMoveCandidateStageModal = ({
  jobPostingId,
  onSuccess,
  functionId,
  totalCount,
  ...props
}: BulkMoveCandidateStageModalProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const mutation = useAPIMutation(
    async ({ stageId }: MoveCandidateStageFormValues, api) => {
      await api.post(
        '/recruitment-processes-actions/move-all-to-stage/',
        {
          stage: Number(stageId),
        },
        {
          params: await buildRecruitmentProcessQueryParam(
            searchParams,
            api,
            jobPostingId,
            functionId
          ),
        }
      );
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('useSearchCandidates');
        await queryClient.invalidateQueries('/job-postings/');
        await queryClient.invalidateQueries(`/job-postings/${jobPostingId}/`);
        await queryClient.invalidateQueries('/recruitment-processes/');
        await queryClient.invalidateQueries('/candidate-history/');
      },
      onSuccess: async (data, variables, context) => {
        message.success(t('Candidato(s) movido(s).'));

        onSuccess && onSuccess(data, variables, context);
      },
      onError: (err, variables, context) => {
        if (err?.response?.data?.error === 'empty_queryset_move_all_to_stage') {
          return notification.error({
            duration: 0,
            message: t('Erro'),
            description: t(
              'Não é possível realizar esta ação, pois não há candidatos a serem selecionados. Recarregue a página e tente novamente.'
            ),
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
    }
  );

  return (
    <>
      <MoveCandidateStageModal
        jobPostingId={jobPostingId}
        helmetTitle={t('Mover {{totalCount}} pessoa(s)', { totalCount: totalCount || '...' })}
        title={
          <Typography.Text>
            <Trans>Mover</Trans>{' '}
            <Typography.Text strong>
              {totalCount || '...'} <Trans>pessoa(s)</Trans>
            </Typography.Text>
          </Typography.Text>
        }
        loading={mutation.isLoading}
        onFinish={async (values) => {
          mutation.mutate(values);
        }}
        isHiredStageDisabled
        {...props}
      />
    </>
  );
};

export default BulkMoveCandidateStageModal;
